// Runtime settings cache + debounced save scheduler.
//
// The cache is the source of truth at runtime so the existing
// load*/save* helpers in the calendar / voyage-details stores can stay
// synchronous (initialise stores during render). Calling `initSettings`
// once on app start populates the cache from the backend; from then on
// reads are instant and writes go to the cache + a debounced PUT.
//
// The cache is backed by a Solid signal so consumers in reactive
// contexts (createMemo / JSX) auto-track changes. A plain `let cache`
// would silently mutate without re-rendering — that bit us when kind
// colour changes didn't apply to the timeline until a manual refresh.
//
// If `initSettings` hasn't completed yet (or fails outright), reads
// return the in-memory defaults so the app remains usable. The first
// successful fetch overwrites whatever was in the cache, so a brief
// flash of "no saved colours" is possible on the very first paint —
// that's the cost of avoiding a Suspense boundary around the whole app.

import { createSignal } from "solid-js";
import { DEFAULT_SETTINGS, type Settings } from "../../../../backend/shared";
import { fetchSettings, putSettings } from "./client";

const SAVE_DEBOUNCE_MS = 500;

const [cacheGet, setCacheSignal] = createSignal<Settings>(
  { ...DEFAULT_SETTINGS, portColors: { ...DEFAULT_SETTINGS.portColors } },
  // Always notify on writes — Settings ships a fresh object every time
  // anyway, but be explicit so a future micro-optimisation that mutates
  // in place doesn't silently break reactivity.
  { equals: false },
);
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let pendingSave: Promise<void> | null = null;

// One-shot init kicked off from App.tsx onMount. Subsequent calls re-fetch
// (rare — only really useful if we add multi-tab sync later).
export async function initSettings(): Promise<Settings> {
  try {
    const fresh = await fetchSettings();
    setCacheSignal(fresh);
    return fresh;
  } catch (err) {
    console.warn("[settings] initial fetch failed, using defaults:", err);
    return cacheGet();
  }
}

// Synchronous getters / setters. Reads inside createMemo / JSX
// auto-subscribe to the underlying signal, so writes propagate
// reactively. Setters trigger a debounced PUT.
export function getCache(): Settings {
  return cacheGet();
}

export function setPortColors(next: Record<string, string>): void {
  setCacheSignal({ ...cacheGet(), portColors: { ...next } });
  scheduleSave();
}

export function setEventKindColors(next: Record<string, string>): void {
  setCacheSignal({ ...cacheGet(), eventKindColors: { ...next } });
  scheduleSave();
}

function scheduleSave(): void {
  if (saveTimer !== null) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null;
    pendingSave = putSettings(cacheGet()).catch((err) => {
      console.warn("[settings] save failed:", err);
    });
  }, SAVE_DEBOUNCE_MS);
}

// Useful for tests + a clean shutdown — flushes any pending save.
export async function flushSettingsSave(): Promise<void> {
  if (saveTimer !== null) {
    clearTimeout(saveTimer);
    saveTimer = null;
    pendingSave = putSettings(cacheGet()).catch((err) => {
      console.warn("[settings] save failed:", err);
    });
  }
  if (pendingSave) await pendingSave;
}

// Synchronous best-effort flush for `pagehide` — same shape as the
// async path, but doesn't await pendingSave because the page may already
// be unloading by the time fetch() resumes. `keepalive: true` on the
// PUT below tells the browser to let the request finish even after
// teardown, so the user's last colour pick / panel-height drag actually
// reaches the server.
export function flushSettingsSaveSync(): void {
  if (saveTimer === null) return;
  clearTimeout(saveTimer);
  saveTimer = null;
  try {
    void fetch("/api/settings", {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(cacheGet()),
      keepalive: true,
    });
  } catch (err) {
    console.warn("[settings] pagehide flush failed:", err);
  }
}

// Wire pagehide once at module import. Capture phase + pagehide
// (rather than beforeunload) so the flush also fires when the page
// is bfcache-suspended on iOS / Firefox — beforeunload doesn't fire
// in that path.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flushSettingsSaveSync, { capture: true });
}

// Test-only — reset to defaults without touching the network.
export function _resetCacheForTests(): void {
  setCacheSignal({ ...DEFAULT_SETTINGS, portColors: { ...DEFAULT_SETTINGS.portColors } });
  if (saveTimer !== null) { clearTimeout(saveTimer); saveTimer = null; }
  pendingSave = null;
}
